import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentStore } from '../../stores/agentStore';
import { surfaceColor, surfaceLabel } from '../../utils/agentSurfaces';
import { copyTextToClipboard } from '../../utils/clipboard';
import { REQUESTS_PATH } from '../../routes';
import ResizableDrawer from '../ResizableDrawer';
import TurnKindChip, { isAutomatedTurn } from './TurnKindChip';

// Compact markdown styling for the agent answer inside the transcript.
const markdownSx = {
  flex: 1,
  fontSize: '0.875rem',
  color: '#cbd5e1',
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
  '& p': { my: 0.75, lineHeight: 1.6 },
  '& h1, & h2, & h3, & h4': { fontSize: '0.95rem', fontWeight: 600, color: '#f1f5f9', mt: 1.5, mb: 0.5 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& li': { mb: 0.25 },
  '& strong': { color: '#f1f5f9' },
  '& a': { color: '#60a5fa' },
  '& code': {
    bgcolor: 'rgba(148, 163, 184, 0.15)',
    px: 0.5,
    py: 0.1,
    borderRadius: 0.5,
    fontSize: '0.85em',
  },
  '& pre': {
    bgcolor: 'rgba(15, 23, 42, 0.7)',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', px: 0 },
  },
  '& blockquote': {
    borderLeft: '3px solid rgba(148, 163, 184, 0.4)',
    pl: 1.5,
    ml: 0,
    color: '#94a3b8',
  },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.85em' },
  '& th, & td': { border: '1px solid rgba(148, 163, 184, 0.2)', p: 0.5, textAlign: 'left' },
  '& th': { bgcolor: 'rgba(148, 163, 184, 0.1)' },
  '& img': { maxWidth: '100%' },
} as const;

function fmtTime(ts: string): string {
  try {
    return format(new Date(ts), 'MMM d, HH:mm');
  } catch {
    return ts;
  }
}

/**
 * Right-side transcript drawer for one agent conversation. Rendered as the
 * `/requests/thread/:threadId` child route, so the URL is the single source of
 * truth: link to that path from anywhere (or share it) to open this panel.
 */
export default function ConversationDrawer() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedConversation,
    isConversationLoading,
    conversationError,
    loadConversation,
    clearSelectedConversation,
  } = useAgentStore();
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const conv = selectedConversation;

  // Clearing on cleanup (unmount or threadId change) also invalidates the
  // in-flight request, so a slow transcript can't land in a panel the user has
  // already closed — including a close via browser Back, which never runs close().
  useEffect(() => {
    if (!threadId) return;
    loadConversation(threadId);
    return clearSelectedConversation;
  }, [threadId, loadConversation, clearSelectedConversation]);

  // Undo the navigation that opened the panel rather than stacking another entry,
  // which would leave Back re-opening what the user just closed. A shared link
  // opened cold has nothing to pop — React Router keys that first entry 'default'
  // — so replace it with the list instead.
  const close = () => {
    if (location.key === 'default') navigate(REQUESTS_PATH, { replace: true });
    else navigate(-1);
  };

  const copyLink = async () => {
    const copied = await copyTextToClipboard(window.location.href);
    setCopyMessage(copied ? 'Transcript link copied' : 'Copy failed — use the URL in the address bar');
  };

  return (
    <ResizableDrawer
      open
      onClose={close}
      minWidth={560}
      storageKey="pd-conversation-width"
      PaperProps={{ sx: { p: 2 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Transcript
        </Typography>
        {conv && (
          <Tooltip title="Copy link to this transcript" arrow>
            <IconButton onClick={copyLink} size="small">
              <LinkIcon />
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={close} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {conversationError ? (
        <Alert severity="error">{conversationError}</Alert>
      ) : isConversationLoading || !conv ? (
        <>
          <Skeleton variant="rounded" height={80} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={120} sx={{ mb: 1 }} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Chip
              label={surfaceLabel(conv.surface)}
              size="small"
              sx={{
                backgroundColor: `${surfaceColor(conv.surface)}22`,
                color: surfaceColor(conv.surface),
                fontWeight: 600,
                height: 22,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {conv.agentName ?? 'Unknown agent'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {conv.orgName ?? conv.orgId ?? '—'} · {conv.userName ?? conv.userId ?? '—'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {conv.turns.map((turn) => (
            <Box key={turn.traceId} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {fmtTime(turn.timestamp)}
                  {turn.latency != null ? ` · ${turn.latency.toFixed(1)}s` : ''}
                  {turn.successStatus === false ? ' · failed' : ''}
                </Typography>
                <TurnKindChip kind={turn.turnKind} />
              </Box>

              {turn.userRequest && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {isAutomatedTurn(turn.turnKind) ? (
                    <AutorenewIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                  ) : (
                    <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {turn.userRequest}
                  </Typography>
                </Box>
              )}

              {turn.answer && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(139,92,246,0.06)',
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.3 }} />
                  <Box sx={markdownSx}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.answer}</ReactMarkdown>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </>
      )}

      <Snackbar
        open={copyMessage !== null}
        autoHideDuration={3000}
        onClose={() => setCopyMessage(null)}
        message={copyMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </ResizableDrawer>
  );
}
